import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X } from 'lucide-react';

export default function NotificationPrompt() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Show prompt after 5 seconds if not dismissed
    const timer = setTimeout(() => {
      if (!localStorage.getItem('notification-prompt-dismissed')) {
        setVisible(true);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    localStorage.setItem('notification-prompt-dismissed', 'true');
  };

  const handleEnable = () => {
    // Mock: show a notification after 30 seconds
    setVisible(false);
    localStorage.setItem('notification-prompt-dismissed', 'true');

    if ('Notification' in window) {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          setTimeout(() => {
            new Notification(t('notifications.mock_title'), {
              body: t('notifications.mock_message'),
              icon: '/icon-192.png',
            });
          }, 30000);
        }
      });
    } else {
      // Fallback mock: show an alert-like toast after 30s
      setTimeout(() => {
        const toast = document.createElement('div');
        toast.className = 'fixed bottom-4 right-4 bg-zinc-900 border border-amber-500 text-white px-4 py-3 rounded-lg shadow-2xl z-[200] flex items-center gap-3 max-w-sm';
        toast.innerHTML = `<Bell className="w-5 h-5 text-amber-500 shrink-0" /><div><div class="font-bold text-sm">${t('notifications.mock_title')}</div><div class="text-xs text-zinc-400">${t('notifications.mock_message')}</div></div>`;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 5000);
      }, 30000);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[200] max-w-sm bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl p-4 animate-in slide-in-from-bottom-4">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors"
        aria-label={t('notification_prompt.dismiss')}
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h4 className="font-display text-sm uppercase text-white mb-1">
            {t('notifications.prompt_title')}
          </h4>
          <p className="text-zinc-400 text-xs leading-relaxed mb-3">
            {t('notifications.prompt_message')}
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleEnable}
              className="bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 font-bold uppercase tracking-wider text-[10px] transition-colors"
            >
              {t('notifications.enable')}
            </button>
            <button
              onClick={handleDismiss}
              className="text-zinc-400 hover:text-white px-3 py-1.5 font-mono uppercase tracking-wider text-[10px] transition-colors"
            >
              {t('notifications.dismiss')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
